import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';

const ItemizedBillList = ({ items, onItemSelect, onItemUpdate, onItemAdd, participants = [], initialSelectedItems = [] }) => {
  // Inicializar con los items ya seleccionados (validados)
  const [selectedItems, setSelectedItems] = useState(() => new Set(initialSelectedItems));
  const [editingItem, setEditingItem] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [showAddModal, setShowAddModal] = useState(false);
  const [newItemValues, setNewItemValues] = useState({
    name: '',
    quantity: 1,
    unitPrice: 0,
    category: 'otros'
  });
  const [showShareModalFor, setShowShareModalFor] = useState(null);
  const [shareSelectionMap, setShareSelectionMap] = useState({});

  // 🔄 Sincronizar cuando initialSelectedItems cambia (por ejemplo, al cargar desde Supabase)
  useEffect(() => {
    if (initialSelectedItems && initialSelectedItems.length > 0) {
      console.log('🔄 Sincronizando items seleccionados:', initialSelectedItems);
      setSelectedItems(new Set(initialSelectedItems));
    }
  }, [initialSelectedItems]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    })?.format(amount);
  };

  const handleItemToggle = (itemId) => {
    const newSelected = new Set(selectedItems);
    if (newSelected?.has(itemId)) {
      newSelected?.delete(itemId);
    } else {
      newSelected?.add(itemId);
    }
    setSelectedItems(newSelected);
    onItemSelect && onItemSelect(Array.from(newSelected));
  };

  const handleSelectAll = () => {
    const allItemIds = items?.map(item => item.id) || [];
    const newSelected = new Set(allItemIds);
    setSelectedItems(newSelected);
    onItemSelect && onItemSelect(Array.from(newSelected));
  };

  const handleDeselectAll = () => {
    setSelectedItems(new Set());
    onItemSelect && onItemSelect([]);
  };

  const handleEditItem = (item, e) => {
    e.stopPropagation(); // Evitar que se seleccione el item
    setEditingItem(item.id);
    setEditValues({
      quantity: item.quantity,
      unitPrice: item.unitPrice
    });
  };

  const handleCancelEdit = (e) => {
    e?.stopPropagation();
    setEditingItem(null);
    setEditValues({});
  };

  const handleSaveEdit = (item, e) => {
    e.stopPropagation();

    const newQuantity = parseFloat(editValues.quantity) || item.quantity;
    const newUnitPrice = parseFloat(editValues.unitPrice) || item.unitPrice;
    const newTotalPrice = newQuantity * newUnitPrice;

    if (onItemUpdate) {
      onItemUpdate(item.id, {
        quantity: newQuantity,
        unitPrice: newUnitPrice,
        totalPrice: newTotalPrice
      });
    }

    setEditingItem(null);
    setEditValues({});
  };

  const handleEditValueChange = (field, value) => {
    setEditValues(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddItem = () => {
    if (!newItemValues.name.trim()) {
      return;
    }

    const quantity = parseFloat(newItemValues.quantity) || 1;
    const unitPrice = parseFloat(newItemValues.unitPrice) || 0;
    const totalPrice = quantity * unitPrice;

    const newItem = {
      id: `item-${Date.now()}`,
      name: newItemValues.name.trim(),
      description: '',
      category: newItemValues.category,
      quantity: quantity,
      unitPrice: unitPrice,
      totalPrice: totalPrice,
      tags: []
    };

    if (onItemAdd) {
      onItemAdd(newItem);
    }

    // Reset form
    setNewItemValues({
      name: '',
      quantity: 1,
      unitPrice: 0,
      category: 'otros'
    });
    setShowAddModal(false);
  };

  const handleNewItemValueChange = (field, value) => {
    setNewItemValues(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const openShareModal = (item) => {
    const current = (item?.sharedWith || []).slice();
    setShareSelectionMap(prev => ({
      ...prev,
      [item.id]: new Set(current)
    }));
    setShowShareModalFor(item.id);
  };

  const toggleParticipantForItem = (itemId, participantId) => {
    setShareSelectionMap(prev => {
      const next = { ...prev };
      const setFor = new Set(next[itemId] || []);
      if (setFor.has(participantId)) setFor.delete(participantId);
      else setFor.add(participantId);
      next[itemId] = setFor;
      return next;
    });
  };

  const saveShareSelection = (item) => {
    const selectedSet = shareSelectionMap[item.id] || new Set();
    const sharedWith = Array.from(selectedSet);
    const updates = {
      shared: sharedWith.length > 0,
      sharedWith
    };
    onItemUpdate && onItemUpdate(item.id, updates);
    setShowShareModalFor(null);
  };

  const getCategoryIcon = (category) => {
    const categoryIcons = {
      'entrantes': 'Utensils',
      'principales': 'ChefHat',
      'postres': 'Cookie',
      'bebidas': 'Coffee',
      'vinos': 'Wine',
      'otros': 'MoreHorizontal'
    };
    return categoryIcons?.[category] || 'Utensils';
  };

  const groupedItems = items?.reduce((acc, item) => {
    const category = item?.category || 'otros';
    if (!acc?.[category]) {
      acc[category] = [];
    }
    acc?.[category]?.push(item);
    return acc;
  }, {});

  const categoryNames = {
    'entrantes': 'Entrantes',
    'principales': 'Platos Principales',
    'postres': 'Postres',
    'bebidas': 'Bebidas',
    'vinos': 'Vinos y Licores',
    'otros': 'Otros'
  };

  const allSelected = items?.length > 0 && selectedItems.size === items?.length;
  const shareModalItem = items?.find(i => i.id === showShareModalFor);

  return (
    <div className="bg-surface border border-border rounded-lg overflow-hidden w-full">
      <div className="p-4 bg-muted border-b border-border">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-3 flex-wrap">
            <h3 className="text-lg font-semibold text-foreground whitespace-nowrap">Artículos de la Cuenta</h3>
            <span className="text-sm text-text-secondary hidden sm:inline">
              — Revisa los artículos antes de dividir
            </span>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-primary hover:bg-primary/10 rounded-lg transition-colors"
            >
              <Icon name="Plus" size={16} />
              Añadir
            </button>
            {!allSelected && (
              <button
                onClick={handleSelectAll}
                className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-primary hover:bg-primary/10 rounded-lg transition-colors"
              >
                <Icon name="CheckSquare" size={16} />
                <span className="hidden sm:inline">Seleccionar todos</span>
                <span className="sm:hidden">Todos</span>
              </button>
            )}
            {selectedItems.size > 0 && (
              <button
                onClick={handleDeselectAll}
                className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-text-secondary hover:bg-muted rounded-lg transition-colors"
              >
                <Icon name="Square" size={16} />
                <span className="hidden sm:inline">Deseleccionar</span>
              </button>
            )}
          </div>
        </div>
      </div>
      <div className="max-h-96 overflow-y-auto overflow-x-hidden">
        {Object.entries(groupedItems)?.map(([category, categoryItems]) => (
          <div key={category} className="border-b border-border last:border-b-0">
            {/* Category Header */}
            <div className="sticky top-0 bg-muted/80 backdrop-blur-sm px-4 py-2 border-b border-border">
              <div className="flex items-center space-x-2">
                <Icon name={getCategoryIcon(category)} size={16} className="text-primary" />
                <span className="text-sm font-medium text-foreground">
                  {categoryNames?.[category]}
                </span>
                <span className="text-xs text-text-secondary">
                  ({categoryItems?.length} artículos)
                </span>
              </div>
            </div>

            {/* Category Items */}
            <div className="divide-y divide-border">
              {categoryItems?.map((item) => (
                <div
                  key={item?.id}
                  className={`
                    p-4 hover:bg-muted/30 transition-colors cursor-pointer
                    ${selectedItems?.has(item?.id) ? 'bg-primary/5 border-l-4 border-l-primary' : ''}
                  `}
                  onClick={() => handleItemToggle(item?.id)}
                >
                  <div className="flex flex-col md:flex-row items-start md:items-center justify-between min-w-0 w-full overflow-hidden">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0 mt-1">
                          <div className={`
                            w-5 h-5 rounded border-2 flex items-center justify-center transition-colors
                            ${selectedItems?.has(item?.id)
                              ? 'bg-primary border-primary text-primary-foreground'
                              : 'border-border hover:border-primary'
                            }
                          `}>
                            {selectedItems?.has(item?.id) && (
                              <Icon name="Check" size={12} />
                            )}
                          </div>
                        </div>

                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-medium text-foreground break-words">
                            {item?.name}
                          </h4>
                          {item?.description && (
                            <p className="text-xs text-text-secondary mt-1 line-clamp-2 break-words">
                              {item?.description}
                            </p>
                          )}

                          {/* Edit Mode */}
                          {editingItem === item?.id ? (
                            <div className="flex items-center space-x-3 mt-2" onClick={(e) => e.stopPropagation()}>
                              <div className="flex items-center space-x-2">
                                <label className="text-xs text-text-secondary">Cant:</label>
                                <Input
                                  type="number"
                                  min="1"
                                  step="1"
                                  value={editValues.quantity}
                                  onChange={(e) => handleEditValueChange('quantity', e.target.value)}
                                  className="w-20 h-8 text-sm"
                                />
                              </div>
                              <div className="flex items-center space-x-2">
                                <label className="text-xs text-text-secondary">Precio:</label>
                                <Input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={editValues.unitPrice}
                                  onChange={(e) => handleEditValueChange('unitPrice', e.target.value)}
                                  className="w-24 h-8 text-sm"
                                />
                              </div>
                              <button
                                onClick={(e) => handleSaveEdit(item, e)}
                                className="px-2 py-1 text-xs bg-primary text-primary-foreground rounded hover:bg-primary/90"
                              >
                                Guardar
                              </button>
                              <button
                                onClick={handleCancelEdit}
                                className="px-2 py-1 text-xs text-text-secondary hover:bg-muted rounded"
                              >
                                Cancelar
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center flex-wrap space-x-4 mt-2">
                              <div className="flex items-center space-x-1">
                                <Icon name="Hash" size={12} className="text-text-secondary" />
                                <span className="text-xs text-text-secondary">
                                  Cantidad: {item?.quantity}
                                </span>
                              </div>
                              <div className="flex items-center space-x-1">
                                <Icon name="Euro" size={12} className="text-text-secondary" />
                                <span className="text-xs text-text-secondary">
                                  Precio unitario: {formatCurrency(item?.unitPrice)}
                                </span>
                              </div>
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={(e) => handleEditItem(item, e)}
                                  className="flex items-center space-x-1 px-2 py-1 text-xs text-primary hover:bg-primary/10 rounded transition-colors"
                                >
                                  <Icon name="Edit2" size={12} />
                                  <span>Editar</span>
                                </button>

                                <button
                                  onClick={(e) => { e.stopPropagation(); if ((participants || []).length <= 1) return; openShareModal(item); }}
                                  className={`flex items-center space-x-1 px-2 py-1 text-xs rounded transition-colors ${((participants || []).length > 1) ? 'text-primary hover:bg-primary/10' : 'text-text-secondary opacity-60 cursor-not-allowed'}`}
                                  title={((participants || []).length > 1) ? (item?.shared ? `Compartido con ${(item.sharedWith || []).length} personas` : 'Compartir entre varios comensales') : 'Añade participantes para poder compartir este artículo'}
                                  aria-disabled={((participants || []).length <= 1)}
                                >
                                  <Icon name="Users" size={12} />
                                  <span>{item?.shared ? `Compartido (${(item.sharedWith || []).length})` : 'Compartir'}</span>
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex-shrink-0 text-right md:ml-4 ml-0 md:mt-0 mt-2 md:w-28 w-full min-w-0">
                      <div className="text-sm font-semibold text-foreground font-mono break-words md:break-normal">
                        {formatCurrency(item?.totalPrice)}
                      </div>
                      {item?.quantity > 1 && (
                        <div className="text-xs text-text-secondary">
                          {item?.quantity} × {formatCurrency(item?.unitPrice)}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Item Tags */}
                  {item?.tags && item?.tags?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2 ml-4">
                      {item?.tags?.map((tag, index) => (
                        <span
                          key={index}
                          className="text-xs bg-accent/20 text-accent-foreground px-2 py-0.5 rounded-full"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      {/* Selection Summary */}
      {selectedItems?.size > 0 && (
        <div className="p-4 bg-primary/5 border-t border-border">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-foreground">
              {selectedItems?.size} artículos seleccionados
            </span>
            <button
              onClick={() => setSelectedItems(new Set())}
              className="text-xs text-primary hover:text-primary/80 transition-colors"
            >
              Limpiar selección
            </button>
          </div>
        </div>
      )}

      {/* Add Item Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-surface rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6 border-b border-border">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-foreground">Añadir Nuevo Artículo</h3>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="text-text-secondary hover:text-foreground transition-colors"
                >
                  <Icon name="X" size={20} />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Nombre del artículo *
                </label>
                <Input
                  type="text"
                  placeholder="Ej: Pizza Margarita"
                  value={newItemValues.name}
                  onChange={(e) => handleNewItemValueChange('name', e.target.value)}
                  className="w-full"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Cantidad *
                  </label>
                  <Input
                    type="number"
                    min="1"
                    step="1"
                    value={newItemValues.quantity}
                    onChange={(e) => handleNewItemValueChange('quantity', e.target.value)}
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Precio unitario (€) *
                  </label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={newItemValues.unitPrice}
                    onChange={(e) => handleNewItemValueChange('unitPrice', e.target.value)}
                    className="w-full"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Categoría
                </label>
                <select
                  value={newItemValues.category}
                  onChange={(e) => handleNewItemValueChange('category', e.target.value)}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="entrantes">Entrantes</option>
                  <option value="principales">Platos Principales</option>
                  <option value="postres">Postres</option>
                  <option value="bebidas">Bebidas</option>
                  <option value="vinos">Vinos y Licores</option>
                  <option value="otros">Otros</option>
                </select>
              </div>

              <div className="pt-2 border-t border-border">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-text-secondary">Total:</span>
                  <span className="text-lg font-semibold text-foreground font-mono">
                    {formatCurrency((newItemValues.quantity || 1) * (newItemValues.unitPrice || 0))}
                  </span>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-border flex gap-3">
              <button
                onClick={() => setShowAddModal(false)}
                className="flex-1 px-4 py-2 text-sm font-medium text-text-secondary bg-muted hover:bg-muted/80 rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleAddItem}
                disabled={!newItemValues.name.trim()}
                className="flex-1 px-4 py-2 text-sm font-medium text-primary-foreground bg-primary hover:bg-primary/90 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Añadir Artículo
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Share Item Modal */}
      {shareModalItem && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-surface rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6 border-b border-border">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-foreground">Compartir artículo: {shareModalItem.name}</h3>
                <button
                  onClick={() => setShowShareModalFor(null)}
                  className="text-text-secondary hover:text-foreground transition-colors"
                >
                  <Icon name="X" size={20} />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <p className="text-sm text-text-secondary">Selecciona las personas con las que compartir este artículo:</p>
              <div className="max-h-48 overflow-y-auto">
                {participants?.map(p => {
                  const selectedSet = shareSelectionMap[shareModalItem.id] || new Set();
                  const checked = selectedSet.has(p.id);
                  return (
                    <label key={p.id} className="flex items-center gap-3 px-2 py-2 hover:bg-muted rounded">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleParticipantForItem(shareModalItem.id, p.id)}
                        className="w-4 h-4"
                      />
                      <span className="text-sm text-foreground">{p.name}</span>
                      <span className="text-xs text-text-secondary ml-auto">{p.id}</span>
                    </label>
                  );
                })}
              </div>

              <div className="text-sm text-text-secondary">
                {(() => {
                  const selectedSet = shareSelectionMap[shareModalItem.id] || new Set();
                  const count = selectedSet.size;
                  const perPerson = count > 0 ? (shareModalItem.totalPrice / count) : 0;
                  return (
                    <div>
                      <div>Seleccionados: <strong>{count}</strong></div>
                      <div>Pago estimado por persona: <strong>{formatCurrency(perPerson)}</strong></div>
                    </div>
                  );
                })()}
              </div>
            </div>

            <div className="p-6 border-t border-border flex gap-3">
              <button
                onClick={() => setShowShareModalFor(null)}
                className="flex-1 px-4 py-2 text-sm font-medium text-text-secondary bg-muted hover:bg-muted/80 rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={() => saveShareSelection(shareModalItem)}
                className="flex-1 px-4 py-2 text-sm font-medium text-primary-foreground bg-primary hover:bg-primary/90 rounded-lg transition-colors"
              >
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ItemizedBillList;